/**
 * Whether the auth server rejected the stored session, as opposed to failing
 * to answer (#440).
 *
 * Only a rejection justifies dropping the session: the account was deleted
 * (403 `user_not_found`), the session was revoked or is gone, or the token is
 * refused (401). A network failure or a 5xx says nothing about the session, and
 * signing the user out for it would log them out every time they go offline.
 *
 * Matched by the errors' own `name` and `status` rather than supabase-js's
 * `isAuthApiError` / `isAuthSessionMissingError`: those are re-exported from
 * `@supabase/auth-js` through `export *`, which the Parcel bundle drops, so
 * they are undefined in the browser. The unit tests construct the real classes.
 */
export function serverRejectedSession(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "AuthSessionMissingError") return true;
  const status = (error as { status?: unknown }).status;
  return error.name === "AuthApiError" && (status === 401 || status === 403);
}

/** The query parameter that marks a bounce from an unreachable auth server. */
export const UNREACHABLE_PARAM = "unreachable";

/**
 * Where the app sends a visitor whose session check failed (#442).
 *
 * A rejected session has been dropped by then, so the plain login page is
 * right. Any other failure keeps the session -- going offline must not log
 * anyone out -- and the login page would find it and send the visitor straight
 * back, in a reload loop for as long as the outage lasts. The parameter tells
 * the login page to stay and say why.
 */
export function loginPathAfterFailedCheck(error: unknown): string {
  return serverRejectedSession(error)
    ? "/login/"
    : `/login/?${UNREACHABLE_PARAM}=1`;
}

/** Whether the login page was reached from an unreachable auth server. */
export function arrivedFromUnreachableServer(search: string): boolean {
  return new URLSearchParams(search).has(UNREACHABLE_PARAM);
}

/** The query parameter that marks a session the server kept rejecting (#515). */
export const SESSION_ENDED_PARAM = "session-ended";

/** Whether the app sent the visitor here because their session ended. */
export function arrivedAfterSessionEnded(search: string): boolean {
  return new URLSearchParams(search).has(SESSION_ENDED_PARAM);
}
